import React, { useState } from 'react';
import { useLocation } from 'wouter';
import './Blog.css';
import BlogYutLogo from './BlogYutLogo.jsx';
import PageVisitsCharts from './PageVisitsCharts.jsx';
import MetricsCounts, { MetricTotal } from './MetricsCounts.jsx';

const DevJournalEntries = [
  {
    date: '03/05/26',
    title: 'Command Center Refactor',
    content: 'Completely refactored the command center system to improve code clarity and maintainability. The selection logic now handles pieces, tiles, finish tokens, and finish stars more elegantly. Mobile performance has been optimized with better state management and reduced re-renders. Major updates to the UI include clearer visual feedback when moves are available and better handling of animation states.'
  },
  {
    date: '03/15/26',
    title: 'Chat & Update Panel Launch',
    content: 'Launched the new chat system with proper Korean character support - thanks to community feedback for catching this bug! Implemented the update panel with scale animations that display sections one at a time, solving the modal overlap issue. Added new UI framework for displaying notifications and alerts. The chat now properly handles text input from Korean keyboards without bugs.'
  },
  {
    date: '03/24/26',
    title: 'Code Refactor & Blog Implementation',
    content: 'Major code cleanup to make the codebase more maintainable and easier to extend. Refactored camera zoom and particle size to scale responsively with screen width, similar to the evaporating stars shader. The update button no longer pause when resizing or switching devices. Began implementation of the HTML blog and how-to-play page for better SEO and accessibility. These pages will complement the 3D experience and showcase our game development capabilities.'
  }
];

const Updates = [
  {
    date: '3/15/26',
    title: 'Mobile Controls Enhanced',
    content: 'MOBILE CONTROLS ARE ENHANCED! Direct the ships with buttons closer to your thumb. Korean characters can now be properly typed in the chatbox. Thank you to Leo and SluggyDoodle for reporting these issues on Discord!'
  }
];

const HowToPlayContent = {
  overview: 'Yutnori (윷놀이) is a traditional Korean board game played for centuries, especially during Korean New Year celebrations. It\'s a race game where players move their pieces around a board based on the throw of four wooden sticks called "yut".',
  goal: 'The goal is to move all your tokens around the board and reach the finish before the other team does.',
  yutRules: [
    { name: 'Do (도)', spaces: '1 space' },
    { name: 'Ge (개)', spaces: '2 spaces' },
    { name: 'Gul (걸)', spaces: '3 spaces' },
    { name: 'Yut (윷)', spaces: '4 spaces + extra throw' },
    { name: 'Mo (모)', spaces: '5 spaces + extra throw' },
    { name: 'Backdo (뒷도)', spaces: '-1 space (skip turn if no tokens on board)' },
    { name: 'Nak (낙)', spaces: '0 spaces (sticks out of bounds)' }
  ],
  keyRules: [
    'If you roll Yut or Mo, you get an extra throw',
    'You can catch enemy tokens by landing on the same space (they return home if not on a star)',
    'Star spaces (★) are safe zones where enemies cannot catch you',
    'The first team to move all their pieces to the finish wins',
    'You can stack your own pieces and ride together on the board'
  ],
  resources: [
    { title: 'Traditional Yutnori Rules', url: 'https://en.wikipedia.org/wiki/Yut' },
    { title: 'Korean Games & Culture', url: 'https://www.korea.net/About-Korea/Culture' },
    { title: 'Yutnori Museum', url: 'https://www.korea.net/About-Korea/Culture' }
  ]
};

export default function Blog() {
  const [location] = useLocation();
  const activeTab = {
    '/blog/howtoplay': 'how-to-play',
    '/blog/updates': 'updates',
    '/blog/metrics': 'metrics'
  }[location] ?? 'how-to-play';
  const [activeDevEntry, setActiveDevEntry] = useState(0);
  const [activeUpdate, setActiveUpdate] = useState(0);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: '#fade76', color: '#111827' }}>
      {/* Navigation Bar */}
      <nav className="blog-nav">
        <div className="blog-nav-container">
          <div className="blog-logo">
            <BlogYutLogo />
            <div>
              <span className="blog-title">YUT<br />NORI<br />UNIVERSE</span>
            </div>
          </div>
          <div className="blog-nav-links">
            <a className={`nav-link ${activeTab === 'how-to-play' ? 'active' : ''}`} href="/blog/howtoplay">
              How To Play
            </a>
            <a
              href="https://yutnori.app"
              className="nav-link"
            >
              Play Now!
            </a>
            <a className={`nav-link ${activeTab === 'updates' ? 'active' : ''}`} href="/blog/updates">
              Updates
            </a>
          </div>
        </div>
      </nav>

      {/* Content Area */}
      <div className="blog-content-area">
        {/* Dev Journal Section */}
        {activeTab === 'dev-journal' && (
          <div className="section-container">
            <div className="section-sidebar">
              <h3 className="sidebar-title">Entries</h3>
              {DevJournalEntries.map((entry, idx) => (
                <button
                  key={idx}
                  className={`sidebar-button ${activeDevEntry === idx ? 'active' : ''}`}
                  onClick={() => setActiveDevEntry(idx)}
                >
                  {entry.date}
                </button>
              ))}
            </div>
            <div className="section-main">
              <div className="entry-card">
                <div className="entry-header">
                  <h2>{DevJournalEntries[activeDevEntry].title}</h2>
                  <span className="entry-date">{DevJournalEntries[activeDevEntry].date}</span>
                </div>
                <p className="entry-text">{DevJournalEntries[activeDevEntry].content}</p>
              </div>
            </div>
          </div>
        )}

        {/* How To Play Section */}
        {activeTab === 'how-to-play' && (
          <div className="section-container">
            <div className="section-main how-to-play-main">
              <div className="entry-card">
                <h2>How To Play Yutnori</h2>
                
                <div className="subsection">
                  <h3>Overview</h3>
                  <p>{HowToPlayContent.overview}</p>
                </div>

                <div className="subsection">
                  <h3>Goal</h3>
                  <p>{HowToPlayContent.goal}</p>
                </div>

                <div className="subsection">
                  <h3>The Yut (Dice)</h3>
                  <p>Four wooden sticks are thrown. The number and orientation of flat sides landing face-up determine your move:</p>
                  <div className="rules-grid">
                    {HowToPlayContent.yutRules.map((rule, idx) => (
                      <div key={idx} className="rule-item">
                        <strong>{rule.name}</strong>
                        <span>{rule.spaces}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="subsection">
                  <h3>Key Rules</h3>
                  <ul className="rules-list">
                    {HowToPlayContent.keyRules.map((rule, idx) => (
                      <li key={idx}>{rule}</li>
                    ))}
                  </ul>
                </div>

                <div className="subsection">
                  <h3>Learn More</h3>
                  <p>For more information about traditional Yutnori:</p>
                  <div className="resources-list">
                    {HowToPlayContent.resources.map((resource, idx) => (
                      <a 
                        key={idx}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="resource-link"
                      >
                        {resource.title} →
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Updates Section */}
        {activeTab === 'updates' && (
          <div className="section-container">
            <div className="section-sidebar">
              <h3 className="sidebar-title">Updates</h3>
              {Updates.map((update, idx) => (
                <button
                  key={idx}
                  className={`sidebar-button ${activeUpdate === idx ? 'active' : ''}`}
                  onClick={() => setActiveUpdate(idx)}
                >
                  {update.date}
                </button>
              ))}
            </div>
            <div className="section-main">
              <div className="entry-card">
                <div className="entry-header">
                  <h2>{Updates[activeUpdate].title}</h2>
                  <span className="entry-date">{Updates[activeUpdate].date}</span>
                </div>
                <p className="entry-text">{Updates[activeUpdate].content}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'metrics' && (
          <div className="section-container">
            <div className="section-main">
              <div className="entry-card">
                <h1>Metrics</h1>
                <div className="metrics-chart-heading">
                  <h2 className="metrics-heading">Page Visits</h2>
                  <MetricTotal label="Page Visits" surface="home" action="page_view" category="page_view" />
                </div>
                <PageVisitsCharts />
                <div className="metrics-chart-heading">
                  <h2 className="metrics-heading">Games Played</h2>
                  <MetricTotal label="Games Played" surface="lobby" action="start_game" category="gameplay" />
                </div>
                <PageVisitsCharts surface="lobby" action="start_game" category="gameplay" />
                <MetricsCounts />
              </div>
            </div>
          </div>
        )}

        {/* Play Now Section */}
        {activeTab === 'play-now' && (
          <div className="section-container">
            <div className="section-main">
              <div className="entry-card play-now-card">
                <h2>Ready to Play?</h2>
                <p>Experience the classic Korean board game Yutnori with 3D graphics, real-time multiplayer, and beautiful animations!</p>
                <a 
                  href="https://yutnori.app" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="play-now-button"
                >
                  Play Yutnori Now
                </a>
                <p className="play-now-subtitle">No downloads • No registration • Free to play</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}